import React, { useState, useEffect } from "react";
import authFetch from "../authFetch";

/* ══ TRANSLATE INLINE ════════════════════════════════════════
   Wraps any block of user-typed freeform content (expense notes,
   task descriptions, comments, observations) and offers a one-click
   "🌐 Translate" affordance when the viewer's language doesn't match
   the content's language. Click → fetches a Claude Haiku translation,
   caches it in localStorage by content hash, and renders the
   translation below the original with a "show original" toggle.

   Never mutates the source content. Never blocks the render. The
   user's own voice stays intact and the translation is a per-viewer
   convenience.

   Props:
     text     — the freeform string to render
     lang     — viewer's lang ("ar" | "en")
     domain   — short hint passed to the prompt ("note", "description",
                 "comment", "title"). Defaults to "text".
     style    — passthrough to the wrapper span
     compact  — if true, button shows just the icon + the translation
                replaces the original instead of stacking under it.
                Useful in tight rows like task subtitles. */

const CACHE_KEY = "cmdInlineI18n";
const CACHE_MAX = 1500;
const AR_RE = /[؀-ۿ]/;
const isArabic = (s) => typeof s === "string" && AR_RE.test(s);

function readCache() {
  try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "{}"); }
  catch { return {}; }
}
function writeCache(obj) {
  try {
    const keys = Object.keys(obj);
    if (keys.length > CACHE_MAX) {
      const trimmed = {};
      keys.slice(-CACHE_MAX).forEach(k => { trimmed[k] = obj[k]; });
      obj = trimmed;
    }
    localStorage.setItem(CACHE_KEY, JSON.stringify(obj));
  } catch {}
}

/* Canonical glossary pinned into every translation prompt so the same
   English source term always maps to the same Arabic term across notes,
   descriptions, comments, etc. Without this, each independent call
   picks its own synonym (النفقات vs مصاريف, etc.) and the app feels
   stitched together. Add new entries as inconsistencies surface. */
const GLOSSARY = {
  enToAr: {
    "expense":"مصاريف","expenses":"المصاريف",
    "income":"دخل","incomes":"الدخل",
    "realised":"محقق","unrealised":"غير محقق",
    "recurring":"متكرر","one-off":"دفعة واحدة","one off":"دفعة واحدة",
    "investment":"استثمار","investments":"الاستثمارات",
    "approved":"معتمد","pending":"معلق","rejected":"مرفوض","cancelled":"ملغي",
    "vehicle":"مركبة","driver":"السائق","trip":"رحلة",
    "maintenance":"صيانة","insurance":"تأمين",
    "category":"فئة","subcategory":"فئة فرعية",
    "owner":"المالك","staff":"الموظفون","household":"المنزل",
    "supplier":"مورد","project":"مشروع","milestone":"معلم",
    "facility":"المنشأة","property":"عقار","portfolio":"المحفظة",
    "salary":"راتب","loan":"قرض","invoice":"فاتورة","receipt":"إيصال",
    "due":"مستحق","overdue":"متأخر","paid":"مدفوع",
  },
  arToEn: {
    "مصاريف":"expense","الدخل":"income",
    "محقق":"realised","غير محقق":"unrealised",
    "متكرر":"recurring","دفعة واحدة":"one-off",
    "استثمار":"investment","الاستثمارات":"investments",
    "معتمد":"approved","معلق":"pending","مرفوض":"rejected","ملغي":"cancelled",
    "مركبة":"vehicle","السائق":"driver","رحلة":"trip",
    "صيانة":"maintenance","تأمين":"insurance",
    "فئة":"category","فئة فرعية":"subcategory",
  },
};
function buildGlossaryClause(target) {
  const map = target === "ar" ? GLOSSARY.enToAr : GLOSSARY.arToEn;
  const lines = Object.entries(map).map(([k,v]) => `${k} → ${v}`).join("; ");
  return `\n\nUse this glossary for consistency (term → preferred translation): ${lines}.`;
}

const pending = new Map();
async function translate(text, target, domain) {
  const dir = target === "ar" ? "ar" : "en";
  const key = `${dir}:${text}`;
  const cache = readCache();
  /* Ignore cache entries equal to the source — those are stale failures
     from the old fallback that cached `text` when /api/ai-chat returned
     an error. Treat them as "no translation" so the user can retry. */
  if (cache[key] !== undefined && cache[key] !== text) return cache[key];
  if (pending.has(key)) return pending.get(key);

  const p = (async () => {
    const targetName = dir === "ar" ? "Arabic" : "English";
    try {
      const r = await authFetch("/api/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          model: "claude-haiku-4-5-20251001",
          max_tokens: Math.min(800, 200 + Math.ceil(text.length / 2)),
          messages: [{
            role: "user",
            content: `Translate this ${domain} to ${targetName}. Preserve formatting, line breaks, and any numbers or proper nouns. Respond with only the translation, no quotes, no extra text.${buildGlossaryClause(dir)}\n\n${domain}: ${text}`
          }]
        })
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || d.error) throw new Error(d.error || `HTTP ${r.status}`);
      const out = (d.content?.[0]?.text || "").trim().replace(/^["']|["']$/g, "");
      if (!out || out === text) throw new Error("empty or unchanged translation");
      /* Only cache real translations. Failures and no-ops are not cached
         so the next click can retry without hitting a poisoned entry. */
      const next = readCache();
      next[key] = out;
      writeCache(next);
      return out;
    } catch (e) {
      console.warn("[TranslateInline] translate failed:", e?.message || e);
      return null;
    } finally {
      pending.delete(key);
    }
  })();
  pending.set(key, p);
  return p;
}

export default function TranslateInline({ text, lang, domain = "text", style, compact = false }) {
  const targetLang = lang === "ar" ? "ar" : "en";
  const sourceLang = isArabic(text || "") ? "ar" : "en";
  const needsTranslation = !!text && sourceLang !== targetLang;
  const [translated, setTranslated] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [showOriginal, setShowOriginal] = useState(false);

  /* Auto-resolve from cache if present so a paginated list with many
     short strings can render the cached side without showing the
     "Translate" affordance at all. Cached value equal to source means
     a previous failure under the old fallback — treat as no translation. */
  useEffect(() => {
    if (!needsTranslation) {
      setTranslated(null);
      return;
    }
    const cached = readCache()[`${targetLang}:${text}`];
    if (cached && cached !== text) setTranslated(cached);
    else setTranslated(null);
  }, [text, needsTranslation, targetLang]);

  if (!text) return null;
  if (!needsTranslation) return <span style={style}>{text}</span>;

  const fetchIt = async () => {
    if (loading || translated) return;
    setLoading(true);
    setError(false);
    const out = await translate(text, targetLang, domain);
    if (out && out !== text) {
      setTranslated(out);
    } else {
      setError(true);
      setTimeout(() => setError(false), 3000);
    }
    setLoading(false);
  };

  const hasTranslation = !!translated && translated !== text;
  const display = hasTranslation && !showOriginal ? translated : text;
  const isShowingOriginal = !hasTranslation || showOriginal;

  if (compact) {
    return (
      <span style={style}>
        {display}
        <button
          type="button"
          onClick={hasTranslation ? () => setShowOriginal(s => !s) : fetchIt}
          disabled={loading}
          title={error
            ? (lang === "ar" ? "فشلت الترجمة، انقر للمحاولة مرة أخرى" : "Translation failed, click to retry")
            : isShowingOriginal
              ? (lang === "ar" ? "ترجمة" : "Translate")
              : (lang === "ar" ? "إظهار الأصل" : "Show original")}
          style={{
            background: "transparent", border: "none", cursor: "pointer", padding: "0 4px",
            color: error ? "var(--red, #c00)" : "var(--blu, #2563eb)", fontSize: 11, marginInlineStart: 4,
          }}
        >
          {loading ? "…" : error ? "✗" : "🌐"}
        </button>
      </span>
    );
  }

  return (
    <span style={style}>
      {display}
      {hasTranslation ? (
        <button
          type="button"
          onClick={() => setShowOriginal(s => !s)}
          style={{
            background: "transparent", border: "none", cursor: "pointer",
            color: "var(--g400, #888)", fontSize: 11, marginInlineStart: 6, padding: "0 4px",
            fontStyle: "italic",
          }}
        >
          {showOriginal ? (lang === "ar" ? "إظهار الترجمة" : "show translation") : (lang === "ar" ? "إظهار الأصل" : "show original")}
        </button>
      ) : (
        <button
          type="button"
          onClick={fetchIt}
          disabled={loading}
          style={{
            background: error ? "var(--red-bg, #fee)" : "transparent",
            border: `1px solid ${error ? "var(--red-bd, #fcc)" : "var(--g200)"}`,
            cursor: loading ? "default" : "pointer",
            color: error ? "var(--red, #c00)" : "var(--blu, #2563eb)",
            fontSize: 10, padding: "1px 8px",
            borderRadius: 12, marginInlineStart: 6, fontWeight: 600, opacity: loading ? 0.6 : 1,
          }}
          title={error
            ? (lang === "ar" ? "فشلت الترجمة، انقر للمحاولة مرة أخرى" : "Translation failed, click to retry")
            : (lang === "ar" ? "ترجمة بالذكاء الاصطناعي" : "Translate with AI")}
        >
          {loading ? "…" : error ? (lang === "ar" ? "✗ أعد المحاولة" : "✗ Retry") : (lang === "ar" ? "🌐 ترجمة" : "🌐 Translate")}
        </button>
      )}
    </span>
  );
}
